// Enforce required product-form fields on Dawn's product form: text inputs (Name, Last Name,
// Athlete Name, …), digit-only inputs (Athlete Number) and dropdowns (Chapter, …).
//
// The fields (snippets/tv-text-property-field.liquid and snippets/tv-select-property-field.liquid,
// rendered per product tag from snippets/buy-buttons.liquid) are real properties[…] controls
// inside Dawn's own form, so FormData carries their values to the order line without any help from
// JS. What JS is needed for is the *required* rule: Dawn renders the product form with
// `novalidate`, and product-form.js preventDefault()s the submit and posts straight to
// /cart/add.js — so the controls' native `required` attribute never blocks an add.
//
// Enforcement is the capture-phase `submit` listener on document: capture runs before the
// form-level listener product-form.js binds in its constructor, so stopImmediatePropagation()
// stops the add regardless of which script loaded first.
//
// ONE script handles every field on the page by design, whatever its type. A product may carry
// more than one tag (`name` and `last-name` together, or a text field alongside a dropdown), and a
// second copy of this script would fight this one over the button's locked state — with one field
// valid and the other empty, whichever sync() ran last would win and the button would falsely look
// unlocked. So the button state is computed over all of a form's fields in one place. Never add
// a second gate script for a new field type — widen FIELD_SELECTOR and the predicates below.
//
// Dawn owns the submit button's `disabled` attribute (product-info.js toggleSubmitButton drives it
// from variant availability), so this script never writes it — the locked look is a class.
// `aria-disabled` is a free second gate, because product-form.js returns early when it reads
// "true"; it is only removed again when this script was the one that set it, so an in-flight add
// can't be un-gated from under Dawn.
//
// No-ops on any form without a field, so normal product pages are untouched.
(function () {
  if (window.tvTextPropertyGateLoaded) return;
  window.tvTextPropertyGateLoaded = true;

  const FIELD_SELECTOR = '.tv-text-property-input, .tv-select-property-input';
  const BUTTON_SELECTOR = '.product-form__submit';
  const DEFAULT_MAX_LENGTH = 30;

  // A dropdown's value is always one of its own options, so the length rules below — and the
  // normalization that enforces them — are for text inputs only. A <select> has no maxLength, so
  // without this guard maxLengthOf() would fall back to DEFAULT_MAX_LENGTH and (a) reject any
  // option longer than 30 characters outright and (b) let the submit handler assign a truncated
  // string that matches no <option>, silently wiping the shopper's choice.
  function isSelect(field) {
    return field.tagName === 'SELECT';
  }

  // Opt-in via data-tv-digits (snippets/tv-text-property-field.liquid, `digits: true`). For such
  // a field maxLength counts digits rather than characters.
  function isDigits(field) {
    return field.dataset.tvDigits === '1';
  }

  function maxLengthOf(field) {
    return field.maxLength > 0 ? field.maxLength : DEFAULT_MAX_LENGTH;
  }

  // maxlength on the input covers typing and paste; this guards programmatic sets.
  function sanitize(field, value) {
    if (isSelect(field)) return value;
    // Slice the STRING, never parse a Number: a leading zero is significant here, because "00"
    // is a distinct athlete number and must not collapse to "0".
    if (isDigits(field)) return value.replace(/\D/g, '').slice(0, maxLengthOf(field));
    return value.slice(0, maxLengthOf(field));
  }

  function isValid(field) {
    const trimmed = field.value.trim();
    if (isSelect(field)) return trimmed.length > 0;
    // 1..maxLength digits, so a 2-digit field accepts 0-99 including "00", and blank is invalid.
    if (isDigits(field)) return new RegExp('^\\d{1,' + maxLengthOf(field) + '}$').test(trimmed);
    return trimmed.length > 0 && trimmed.length <= maxLengthOf(field);
  }

  function isOurField(node) {
    return !!node && typeof node.matches === 'function' && node.matches(FIELD_SELECTOR);
  }

  function fieldsIn(form) {
    if (!form || typeof form.querySelectorAll !== 'function') return [];
    return Array.prototype.slice.call(form.querySelectorAll(FIELD_SELECTOR));
  }

  function hintFor(field) {
    return document.getElementById(field.getAttribute('aria-describedby'));
  }

  // Hint visibility for one field. Button state is a whole-form concern — see syncButton.
  function syncField(field) {
    const hint = hintFor(field);
    // Don't stomp a transient message flash() is showing, and stay quiet until the shopper has
    // actually left the field or tried to add — an error on page load reads as a bug.
    if (!hint || hint.dataset.tvFlash) return;
    if (field.dataset.tvEmptyMsg) hint.textContent = field.dataset.tvEmptyMsg;
    hint.hidden = isValid(field) || !field.dataset.tvTouched;
  }

  // Visual state only; the submit block below is the real guarantee. The button unlocks only when
  // EVERY field on the form is valid.
  function syncButton(form) {
    const button = form ? form.querySelector(BUTTON_SELECTOR) : null;
    if (!button) return;

    const ok = fieldsIn(form).every(isValid);
    button.classList.toggle('tv-field-locked', !ok);
    if (ok) {
      // Only clear aria-disabled if we set it: product-form.js sets the same attribute for the
      // duration of an add, and clearing that would allow a double submit.
      if (button.dataset.tvLocked) {
        delete button.dataset.tvLocked;
        button.removeAttribute('aria-disabled');
      }
    } else {
      button.dataset.tvLocked = '1';
      button.setAttribute('aria-disabled', 'true');
    }
  }

  function sync(field) {
    syncField(field);
    syncButton(field.form);
  }

  // Briefly surface the constraint when input had to be cleaned (an over-length programmatic set),
  // then revert to the normal gate state. Timers are per-field so two fields flashing at once
  // don't cancel each other.
  const flashTimers = new WeakMap();
  function flash(field) {
    const hint = hintFor(field);
    if (!hint) return;
    hint.textContent = field.dataset.tvFlashMsg || maxLengthOf(field) + ' characters max.';
    hint.hidden = false;
    hint.dataset.tvFlash = '1';
    clearTimeout(flashTimers.get(field));
    flashTimers.set(
      field,
      setTimeout(function () {
        delete hint.dataset.tvFlash;
        sync(field);
      }, 2000),
    );
  }

  document.addEventListener(
    'submit',
    function (event) {
      const form = event.target;
      const fields = fieldsIn(form);
      if (!fields.length) return;

      // The order records the trimmed values. Selects are skipped: assigning anything to one
      // risks matching no <option> and blanking the shopper's choice.
      fields.forEach(function (field) {
        if (isSelect(field)) return;
        field.value = sanitize(field, field.value).trim();
      });

      const firstInvalid = fields.find(function (field) {
        return !isValid(field);
      });

      if (!firstInvalid) {
        fields.forEach(syncField);
        syncButton(form);
        return;
      }

      event.preventDefault();
      event.stopImmediatePropagation();
      // Surface every outstanding error at once, not just the one being focused.
      fields.forEach(function (field) {
        field.dataset.tvTouched = '1';
        syncField(field);
      });
      syncButton(form);
      firstInvalid.focus();
      firstInvalid.scrollIntoView({ block: 'center', behavior: 'smooth' });
    },
    true,
  );

  document.addEventListener('input', function (event) {
    const field = event.target;
    if (!isOurField(field)) return;
    const clean = sanitize(field, field.value);
    if (clean !== field.value) {
      field.value = clean;
      flash(field);
    }
    sync(field);
  });

  // Selects fire `input` in evergreen browsers, but `change` is the canonical event for one.
  // Bind both so a selection can't be missed; sync() is idempotent.
  document.addEventListener('change', function (event) {
    const field = event.target;
    if (!isOurField(field) || !isSelect(field)) return;
    sync(field);
  });

  document.addEventListener(
    'blur',
    function (event) {
      const field = event.target;
      if (!isOurField(field)) return;
      if (!isSelect(field)) field.value = field.value.trim();
      field.dataset.tvTouched = '1';
      sync(field);
    },
    true,
  );

  function syncAll() {
    const forms = new Set();
    document.querySelectorAll(FIELD_SELECTOR).forEach(function (field) {
      syncField(field);
      if (field.form) forms.add(field.form);
    });
    forms.forEach(function (form) {
      syncButton(form);
    });
  }

  document.addEventListener('DOMContentLoaded', syncAll);
  syncAll();
})();
